import type { MetricSuggestion as MetricSuggestionType } from "@runlens/shared";
import {
  CheckIcon,
  XMarkIcon,
  ChartBarSquareIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from "@heroicons/react/24/outline";

interface MetricSuggestionProps {
  suggestion: MetricSuggestionType;
  onAccept: () => void;
  onDecline: () => void;
  isLoading?: boolean;
}

const unitLabels: Record<string, string> = {
  time: "time",
  km: "km",
  "%": "%",
  kg: "kg",
  pace: "pace",
  bpm: "bpm",
};

function formatMetricValue(value: number, unit: string): string {
  switch (unit) {
    case "time": {
      const mins = Math.floor(value / 60);
      const secs = Math.round(value % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    }
    case "pace": {
      const mins = Math.floor(value / 60);
      const secs = Math.round(value % 60);
      return `${mins}:${secs.toString().padStart(2, "0")} /km`;
    }
    case "km":
      return `${value.toFixed(1)} km`;
    case "%":
      return `${Math.round(value)}%`;
    case "kg":
      return `${value.toFixed(1)} kg`;
    case "bpm":
      return `${Math.round(value)} bpm`;
    default:
      return String(value);
  }
}

export function MetricSuggestion({
  suggestion,
  onAccept,
  onDecline,
  isLoading,
}: MetricSuggestionProps) {
  const leadingMetrics = suggestion.metrics.filter(
    (m) => m.metricType === "leading"
  );
  const laggingMetrics = suggestion.metrics.filter(
    (m) => m.metricType === "lagging"
  );

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-indigo-100 rounded-full shrink-0">
          <ChartBarSquareIcon className="w-5 h-5 text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900">
            Suggested Goal Metrics
          </h4>
          <p className="mt-1 text-sm text-gray-600">
            {suggestion.explanation}
          </p>

          {/* Metrics list */}
          <div className="mt-3 space-y-2">
            {leadingMetrics.map((metric, index) => (
              <div
                key={`leading-${index}`}
                className="flex items-center gap-2 text-sm"
              >
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-amber-700 bg-amber-50">
                  {metric.direction === "lower_is_better" ? (
                    <ArrowTrendingDownIcon className="w-3 h-3" />
                  ) : (
                    <ArrowTrendingUpIcon className="w-3 h-3" />
                  )}
                  Leading
                </span>
                <span className="font-medium text-gray-900">
                  {metric.name}
                </span>
                {metric.targetValue && (
                  <span className="text-gray-500">
                    Target:{" "}
                    {formatMetricValue(
                      metric.targetValue.value,
                      metric.unit
                    )}
                  </span>
                )}
                <span className="text-gray-400 text-xs">
                  ({unitLabels[metric.unit] || metric.unit})
                </span>
              </div>
            ))}

            {laggingMetrics.map((metric, index) => (
              <div
                key={`lagging-${index}`}
                className="flex items-center gap-2 text-sm"
              >
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-blue-700 bg-blue-50">
                  {metric.direction === "lower_is_better" ? (
                    <ArrowTrendingDownIcon className="w-3 h-3" />
                  ) : (
                    <ArrowTrendingUpIcon className="w-3 h-3" />
                  )}
                  Lagging
                </span>
                <span className="font-medium text-gray-900">
                  {metric.name}
                </span>
                {metric.targetValue && (
                  <span className="text-gray-500">
                    Target:{" "}
                    {formatMetricValue(
                      metric.targetValue.value,
                      metric.unit
                    )}
                  </span>
                )}
                <span className="text-gray-400 text-xs">
                  ({unitLabels[metric.unit] || metric.unit})
                </span>
              </div>
            ))}
          </div>

          {/* Action buttons */}
          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={onAccept}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
              Add Metrics
            </button>
            <button
              type="button"
              onClick={onDecline}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
